import { EventCodes } from '../../App/model/event-codes';
import type { EventService } from '../../App/services/event-service';
import type { IComponentController, IOnChangesObject, IAugmentedJQuery, ITimeoutService, IScope } from 'angular';
import template from './dashboard-widget.component.html';

import type { WidgetInstance, DashboardService } from '../dashboard.service';
import { DashboardPageController, DashboardConfigurationMode } from '../dashboard-page/dashboard-page.component';
import { SearchGroup } from '../../Shell/PandoraSolutionBuilderAgent/model/search-query/search-form.contracts';
import { DashboardFiltersService } from '../dashboard-filters.service';
import { GridStack, GridStackElement, GridItemHTMLElement } from 'gridstack';

export class DashboardWidgetController implements IComponentController {
	public widgetInstance!: WidgetInstance;
	public serviceContextInfo?: { widgetId: string; viewName: string };
	public page!: DashboardPageController;
	public internalTemplate = '';
	public template?: string;
	public onTitleClick!: (params: { widgetInstance: WidgetInstance }) => void;
	public onSelectItem!: (params: { widgetInstance: WidgetInstance }) => void;
	public fullscreen = false;
	public elId = `mx-widget-instance-${Math.random().toString(36).substring(2, 15)}`;
	private preventLoosingFocusOnFullscreenFn: (event: JQueryEventObject) => void = this.preventLoosingFocusOnFullscreen.bind(this);
	private resizeObserver?: ResizeObserver;

	static readonly $inject = ['$scope', '$element', 'mx.sb.Dashboard.Service', 'mx.sb.Dashboard.DashboardFiltersService', '$timeout', 'mx.shell.EventService'];

	constructor(
		private readonly $scope: IScope,
		private readonly $element: IAugmentedJQuery,
		private readonly dashboardService: DashboardService,
		private readonly dashboardFiltersService: DashboardFiltersService,
		private readonly $timeout: ITimeoutService,
		private readonly eventService: EventService
	) {}

	$onInit() {
		this.setServiceContext(this.widgetInstance);
		this.setInteractiveFiltersFromDashboard();
	}

	$postLink(): void {
		const triggerDrilldownEvent = () => {
			this.$timeout(() => {
				if (this.hasDrilldown()) {
					this.onSelectItem({ widgetInstance: this.widgetInstance });
				}
			});
		};

		this.$element.on('click', '.highcharts-point', (event) => {
			if ($(event.target).closest('mx-org-chart').length === 0) {
				triggerDrilldownEvent();
			}
		});
		this.$element.on('left-click', 'nova-pie-chart', triggerDrilldownEvent);

		this.$element.on('keydown', (event) => {
			if (this.fullscreen && event.key === 'Escape') {
				event.stopPropagation();
				this.$scope.$evalAsync(() => {
					this.toggleFullscreen();
				});
			}
		});

		this.handleAutoHeight();

		this.adjustAria();
	}

	$onDestroy(): void {
		this.resizeObserver?.disconnect();
	}

	$onChanges(onChangesObj: IOnChangesObject) {
		if (onChangesObj.widgetInstance || onChangesObj.template) {
			this.internalTemplate = this.template || '';
			this.setServiceContext(this.widgetInstance);
			this.setInteractiveFiltersFromDashboard();
		}
	}

	public get dataModel() {
		return this.widgetInstance?.dataModel;
	}

	private setServiceContext(widgetInstance: WidgetInstance) {
		this.serviceContextInfo = {
			widgetId: widgetInstance.widgetId ?? '',
			viewName: widgetInstance.templateData?.widgetViewKey ?? '',
		};
	}

	private setInteractiveFiltersFromDashboard() {
		this.$timeout(() => this.dashboardFiltersService.populateInteractiveFilters([this.widgetInstance]), 100);
	}

	public hasDrilldown() {
		return (this.page.configurationMode === DashboardConfigurationMode.Disabled || this.page.embeddedMode) && this.dashboardService.hasDrillDown(this.widgetInstance);
	}

	public getWidgetCategoryClassName() {
		return `mx-dashboard-widget__category-${this.dashboardService.categoryIdAlias(this.widgetInstance?.categoryId)}`;
	}

	public getWidgetClassName() {
		return `mx-dashboard-widget___${this.dashboardService.widgetIdAlias(this.widgetInstance?.widgetId)}`;
	}

	public handleTitleClick() {
		this.dashboardService.unsetSelectedItem(this.widgetInstance);
		this.onTitleClick({ widgetInstance: this.widgetInstance });
	}

	public hasAppliedFilters() {
		const hasAppliedFilterGroup = (filterGroup: SearchGroup | undefined) => {
			return Boolean(filterGroup?.Conditions?.length) || filterGroup?.Groups?.some(hasAppliedFilterGroup);
		};
		const ctx = this.widgetInstance.dataModel?.Context;
		return Boolean(ctx?.KeywordFilter) || hasAppliedFilterGroup(ctx?.DashboardFilters);
	}

	public onChangeLayout() {
		this.dashboardService.changeWidgetInstanceLayout(this.widgetInstance);
	}

	public toggleFullscreen() {
		this.fullscreen = !this.fullscreen;
		this.eventService.dispatchEvent(EventCodes.LayoutChanged, 'sideLayoutResized');

		if (this.fullscreen) {
			this.$element.on('focusout', this.preventLoosingFocusOnFullscreenFn);
		} else {
			this.$element.off('focusout', this.preventLoosingFocusOnFullscreenFn);
		}
	}

	private preventLoosingFocusOnFullscreen(event: JQueryEventObject) {
		if (this.fullscreen && (!event.relatedTarget || !this.$element[0].contains(event.relatedTarget))) {
			this.$element.find('.dashboard-widget--header-buttons_fullscreen').trigger('click');
		}
	}

	private adjustAria() {
		const adjustChartHeadings = () => {
			setTimeout(() => {
				this.$element.find('.chart-container h3').attr('aria-level', '4');
			}, 500);
		};
		adjustChartHeadings();
		this.eventService.subscribeOnce(EventCodes.LayoutChanged, adjustChartHeadings);
		this.eventService.subscribeOnce(EventCodes.AllAjaxComplete, adjustChartHeadings);
	}

	private handleAutoHeight() {
		this.$scope.$watch(
			() =>
				this.widgetInstance?.dataModel?.WidgetConfiguration?.AutoHeight &&
				!this.fullscreen &&
				!this.page?.embeddedMode &&
				this.page?.configurationMode === DashboardConfigurationMode.Disabled,
			(shouldApplyAutoHeight) => {
				if (shouldApplyAutoHeight) {
					const widgetElToObserve = this.$element.find('.dashboard-widget--gridstack-resize-to-content-wrapper');
					widgetElToObserve.css('min-height', `${(Math.max(this.widgetInstance.height, this.widgetInstance.minHeight) - 0.5) * 180}px`);
					if (!this.resizeObserver) {
						this.resizeObserver = new ResizeObserver(
							$.debounce(200, () => {
								GridStack.resizeToContentCB!(this.widgetInstance.el as GridItemHTMLElement);
							})
						);
						this.resizeObserver.observe(widgetElToObserve[0]);
					}
				} else if (this.widgetInstance?.dataModel?.WidgetConfiguration?.AutoHeight) {
					const grid = GridStack.init({}, this.$element[0].closest('.grid-stack') as GridStackElement);
					if (grid) {
						this.resizeObserver?.disconnect();
						this.resizeObserver = undefined;
						grid.update(this.widgetInstance.el as GridItemHTMLElement, {
							h: Math.max(this.widgetInstance.height, this.widgetInstance.minHeight),
							minH: this.widgetInstance.minHeight,
						});
					}
				}
			}
		);
	}
}

export const DashboardWidgetComponent = {
	name: 'mxDashboardWidget',
	controller: DashboardWidgetController,
	controllerAs: 'vm',
	template,
	bindings: {
		widgetInstance: '<',
		template: '<',
		onEdit: '&',
		onRemove: '&',
		onTitleClick: '&',
		onSelectItem: '&',
		page: '<',
	},
};
